// ════════════════════════════════════════════════════════════
//  arts.js — every Impera, U'hlti and Tome, at its CARD price
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-15: "The Tome and Impera U'hlti costs etc will all
//  remain the same as my Terror Cards." So `khao` and `anima` are the
//  printed fares (card-texts.json: stats [Anima, Chao]), and `card`
//  quotes the printed text. The real-time translation is the game's
//  and is labelled per row; the quote is canon and never edited.
//
//  ── CASTING (caP'n's rule) ──────────────────────────────────
//   Level 1 → appears after 3 s, its area highlighted as a warning
//   Level 2 → appears after 1 s, highlighted
//   Level 3 → instant, no warning
//   Impera: no cooldown — Khao is the limit.   Tomes: 10 s cooldown.
//   An Art that touches the adversary is AIMED (free aim): it lands
//   in an area at the cursor and hits whoever is inside when it
//   appears. An Art on yourself just happens when it appears.
//   Casting never roots the caster — the Art manifests on its own.
//
//  ── THE EFFECT LANGUAGE (one row = one Art; a new Art is a row) ─
//   target: "self" | "area" | "global"
//   r: area radius · effects (in order), each one of:
//     { lytToAnima: k }   a hit of the caster's Lyt (+k) straight to Anima
//     { lytHit: k }       a hit of the caster's Lyt (+k) through Lyf as usual
//     { dimLyf: n } { dimAnima: n }        on everyone caught
//     { dimAnimaIfIntangible: n }          Inverted Scales
//     { status: id, dur, on: "self"|"foe"|"team", ... }
//     { lytCharges: n } (extra Lyt Attacks)  { khao: n } ("draw" = Khao)
//     { maxLyf: n, per?: "dragon"|"astraea"|"time" }  { lyt: n, per? }
//     { ruinNonBeings: true }   { ruinOverLyt: 10 }   { ruinBenches: true }
//     { coin: { win: [...], lose: [...] } }  { dice: true }
//     { reviveIlk: "Astraea", count, maxTier }   { construct: {...} }
//     { treasure: {...} }  (a Tome that belongs to the being on the field)
// ════════════════════════════════════════════════════════════

export const TURN = 8;             // seconds that stand in for "this turn" in card text (dial)
export const DRAW_KHAO = 3;        // "draw 1 card" pays this much Khao (dial)
export const CAST_TIME = { 1: 3, 2: 1, 3: 0 };
export const TOME_COOLDOWN = 10;

const A = (o) => ({ target: "self", r: 2.6, effects: [], ...o });

export const ARTS = {
  // ── SPECTRAL TIGER ──────────────────────────────────────────
  spectral_paws: A({ name: "Spectral Paws", kind: "impera", level: 1, khao: 4, anima: 0,
    card: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being.",
    target: "area", r: 2.2, shape: "claw", effects: [{ lytToAnima: 0 }] }),
  spectral_claws: A({ name: "Spectral Claws", kind: "impera", level: 2, khao: 6, anima: 0,
    card: "If this being's Lyt Attack dimmed Anima this turn, dim their Anima by an additional 1.",
    target: "area", r: 2.2, shape: "claw", effects: [{ lytToAnima: 1 }] }),
  haunting_presents: A({ name: "Haunting Presents", kind: "impera", level: 3, khao: 10, anima: 0,
    card: "Op2: Dim the Lyf of your adversary's cards by 2.",
    target: "area", r: 3, shape: "ghost", effects: [{ dimLyf: 2 }] }),
  haunting_presence: A({ name: "Haunting Presence", kind: "uhlti", level: 3, khao: 4, anima: 6,
    card: "*Spectral Tiger U'hlti* OP2: Dim the Lyf of your adversary's cards by 1 and their Anima by 2.",
    target: "area", r: 3.6, shape: "ghost", effects: [{ dimLyf: 1 }, { dimAnima: 2 }] }),

  // ── SHADOW FOX ──────────────────────────────────────────────
  hidden_paws: A({ name: "Hidden Paws", kind: "impera", level: 1, khao: 4, anima: 0,
    card: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being.",
    target: "area", r: 2.2, shape: "claw", effects: [{ lytToAnima: 0 }] }),
  shadow_fur: A({ name: "Shadow Fur", kind: "impera", level: 2, khao: 4, anima: 0,
    card: "OP3: This being is Intagible.", effects: [{ status: "intangible", dur: 2, on: "self" }] }),
  lord_of_shadows: A({ name: "Lord of Shadows", kind: "impera", level: 3, khao: 4, anima: 0,
    card: "OP2: This being is Intagible.", effects: [{ status: "intangible", dur: 2.5, on: "self" }] }),
  royal_darkness: A({ name: "Royal Darkness", kind: "uhlti", level: 3, khao: 4, anima: 6,
    card: "*Shadow Fox U'hlti* Op2: Dim the Lyf of your adversary's cards by 2 and their Anima by 1.",
    target: "area", r: 3.6, shape: "dark", effects: [{ dimLyf: 2 }, { dimAnima: 1 }] }),

  // ── INVERTED SERPENT ────────────────────────────────────────
  phantom_fangs: A({ name: "Phantom Fangs", kind: "impera", level: 1, khao: 4, anima: 0,
    card: "This being's Lyt Attacks can be directed at your adversary's Anima instead of a being.",
    target: "area", r: 2.2, shape: "fang", effects: [{ lytToAnima: 0 }] }),
  supernatural_bite: A({ name: "Supernatural Bite", kind: "impera", level: 2, khao: 6, anima: 0,
    card: "Dim your adversary's Anima by 1, whenever this being Lyt Attacks.",
    target: "area", r: 2.2, shape: "fang", effects: [{ lytHit: 0 }, { dimAnima: 1 }] }),
  inverted_scales: A({ name: "Inverted Scales", kind: "impera", level: 3, khao: 8, anima: 0,
    card: "OPT: Dim your adversary's Anima by 3, for every being they have that is currently Intagible.",
    target: "area", r: 3, shape: "scale", effects: [{ dimAnimaIfIntangible: 3 }] }),
  inverted_fangs: A({ name: "Inverted Fangs", kind: "uhlti", level: 3, khao: 4, anima: 4,
    card: "*Inverted Serpent U'hlti* OP2: If this being's Lyt Attack dimmed Anima this turn, dim their Anima by an additional 2.",
    target: "area", r: 3, shape: "fang", effects: [{ lytToAnima: 2 }] }),

  // ── CHAOS DRAGON ────────────────────────────────────────────
  natural_disaster: A({ name: "Natural Disaster", kind: "impera", level: 1, khao: 9, anima: 0,
    card: "OP2: Ruin 1 Non-Being.",
    target: "area", r: 2.8, shape: "meteor", effects: [{ ruinNonBeings: true }] }),
  thriving_in_chaos: A({ name: "Thriving in Chaos", kind: "impera", level: 2, khao: 8, anima: 0,
    card: "Dim your adversary's Anima by 1, whenever they Lyt Attack.",
    effects: [{ status: "reprisal", dur: 8, on: "self" }] }),
  none_more_than_i: A({ name: "None More Than I", kind: "impera", level: 3, khao: 10, anima: 0,
    card: "Beings with more than 10 Lyt are instantly sent to Necropolis.",
    target: "global", effects: [{ ruinOverLyt: 10 }] }),
  i_or_none: A({ name: "I or None", kind: "uhlti", level: 3, khao: 10, anima: 10,
    card: "*Chaos Dragon U'hlti* OP3: Warlocks must ruin their own beings until each has only 1 being left.",
    target: "global", effects: [{ ruinBenches: true }] }),

  // ── LUCKY DRAGON (Shadow Gambling) ──────────────────────────
  pay_day: A({ name: "Pay Day", kind: "impera", level: 1, khao: 1, anima: 1,
    card: "(SSG) OP2: Dim your own Anima by 1. Flip a coin and choose your side; if it lands on your side, draw 1 card; if not, your adversary draws 1 as well.",
    effects: [{ coin: { win: [{ khao: 3 }], lose: [{ khao: 3 }, { foeKhao: 3 }] } }] }),
  black_cats_best_friend: A({ name: "Black Cat's Best Friend", kind: "impera", level: 2, khao: 7, anima: 0,
    card: "(SSG) OP3: Flip a coin and choose your side; if it lands on your side, draw 2 cards, if not your side, your adversary draws 2 as well.",
    effects: [{ coin: { win: [{ khao: 6 }], lose: [{ khao: 6 }, { foeKhao: 6 }] } }] }),
  fate_maker: A({ name: "Fate Maker", kind: "impera", level: 3, khao: 7, anima: 0,
    card: "(SSG) OP3: If 1 of your cards is going to be Lyt Attacked, flip a coin and choose your side, if it lands on your side, reflect the Lyt Attack.",
    effects: [{ status: "fate", dur: 8, on: "self" }] }),
  dragon_eyes: A({ name: "Dragon Eyes", kind: "uhlti", level: 3, khao: 7, anima: 7,
    card: "*Lucky Dragon U'hlti* (SSG) OP2: Roll 2 dice, if their sum is equal to 2, then you've just won 2 Shadow Gamblings, and you can activate another U'hlti.",
    target: "area", r: 3, shape: "die", effects: [{ dice: true }] }),

  // ── PREMIER DRAGON ──────────────────────────────────────────
  good_morning: A({ name: "Good Morning", kind: "impera", level: 1, khao: 6, anima: 0,
    card: "This being gains +1 Lyt and +1 Lyf for every Dragon Being you have in combat.",
    effects: [{ lyt: 1, per: "dragon" }, { maxLyf: 1, per: "dragon" }] }),
  social_dragonfly: A({ name: "Social Dragonfly", kind: "impera", level: 2, khao: 7, anima: 0,
    card: "This being gains +1 Lyt, for every Dragon Being in combat.",
    effects: [{ lyt: 1, per: "dragonField" }] }),
  destined_for_greatness: A({ name: "Destined For Greatness", kind: "impera", level: 3, khao: 10, anima: 0,
    card: "This being is Intangible to Instincts and U'hltis.",
    effects: [{ status: "destined", dur: 16, on: "self" }] }),
  show_stomper: A({ name: "Show Stomper", kind: "uhlti", level: 3, khao: 10, anima: 10,
    card: "*Premier Dragon U'hlti* Op3: Dim your adversary's Anima by 10.",
    target: "area", r: 3.6, shape: "stomp", effects: [{ dimAnima: 10 }] }),

  // ── Z4Z ─────────────────────────────────────────────────────
  relentless: A({ name: "Relentless", kind: "impera", level: 1, khao: 5, anima: 5,
    card: "OP2: Dim your own Anima by 5; this being gains an additional Lyt Attack.",
    effects: [{ lytCharges: 1 }] }),
  catastrophe: A({ name: "Catastrophe", kind: "impera", level: 2, khao: 8, anima: 0,
    card: "OP2: If you have another Lvl 2 Time Being, this being gains an additional Lyt Attack.",
    effects: [{ lytCharges: 1, needs: "otherChampionTime" }] }),
  apocalypse: A({ name: "Apocalypse", kind: "impera", level: 3, khao: 8, anima: 8,
    card: "OP3: Dim your own Anima by 8; your beings can all make an additional Lyt Attack.",
    effects: [{ lytCharges: 1, team: true }] }),
  apocalypse_now: A({ name: "Apocalypse Now", kind: "uhlti", level: 3, khao: 5, anima: 6,
    card: "*Z4Z U'hlti* This being gains +1 Lyf for every Time Being in combat.",
    effects: [{ maxLyf: 1, per: "timeField" }] }),

  // ── ANTDROID ────────────────────────────────────────────────
  restless: A({ name: "Restless", kind: "impera", level: 1, khao: 5, anima: 5,
    card: "OP3: Dim your own Anima by 5; this being may Lyt Attack again, but only to dim Anima.",
    effects: [{ lytCharges: 1, pierce: true }] }),
  equivalent_exchange: A({ name: "Equivalent Exchange", kind: "impera", level: 2, khao: 2, anima: 4,
    card: "Op2: Dim your own Anima by 4; draw 2 cards.", effects: [{ khao: 6 }] }),
  king_of_the_anthill: A({ name: "King of the Anthill", kind: "impera", level: 3, khao: 10, anima: 0,
    card: "This being can make an additional Lyt Attack for every other \"Antdroid\" being in combat.",
    effects: [{ lytCharges: 1, per: "otherAntdroid" }] }),
  king_of_the_hill: A({ name: "King of the Hill", kind: "uhlti", level: 3, khao: 7, anima: 8,
    card: "*Antdroid U'hlti* OP2: All of your beings' Lyt Attacks can be directed at your adversary's Anima instead of a being.",
    effects: [{ status: "piercing", dur: 16, on: "team" }] }),

  // ── DG-0 ────────────────────────────────────────────────────
  hope: A({ name: "Hope", kind: "impera", level: 1, khao: 5, anima: 0,
    card: "Gain +1 Anima, whenever a new Time Being enters combat.",
    effects: [{ status: "hope", dur: 24, on: "self" }] }),
  law_and_order: A({ name: "Law & Order", kind: "impera", level: 2, khao: 1, anima: 1,
    card: "OP3: Dim your own Anima by 1; draw 1 card.", effects: [{ khao: 3 }] }),
  justice_prevails: A({ name: "Justice Prevails", kind: "impera", level: 3, khao: 7, anima: 0,
    card: "OPT: Draw 1 card, if you gained Anima this turn.", effects: [{ khao: 3, needs: "gainedAnima" }] }),
  justice_reigns: A({ name: "Justice Reigns", kind: "uhlti", level: 3, khao: 6, anima: 8,
    card: "*DG-0 U'hlti* OP2: Send 1 Non-Being to Necropolis or draw 1 card.",
    target: "area", r: 3.2, shape: "light", effects: [{ ruinNonBeings: true, orKhao: 3 }] }),

  // ── H-01 ────────────────────────────────────────────────────
  natural_glow: A({ name: "Natural Glow", kind: "impera", level: 1, khao: 5, anima: 0,
    card: "OP3: This being is Intangible.", effects: [{ status: "intangible", dur: 2, on: "self" }] }),
  natural_lyt: A({ name: "Natural Lyt", kind: "impera", level: 2, khao: 7, anima: 0,
    card: "OP2: Choose 1 of your beings, it must be ruined before your adversary can attack another.",
    effects: [{ status: "bulwark", dur: 16, on: "self" }] }),
  morning_star: A({ name: "Morning Star", kind: "impera", level: 3, khao: 5, anima: 0,
    card: "OP3: Add 1 Astro Being to your hand from your Grimoire or Necropolis.",
    effects: [{ reviveIlk: "Astraea", count: 1, maxTier: "lord" }] }),
  super_nova: A({ name: "Super Nova", kind: "uhlti", level: 3, khao: 8, anima: 4,
    card: "*H-01 U'hlti* OP2: Dim your adversary's Anima by 4.",
    target: "area", r: 3.6, shape: "nova", effects: [{ dimAnima: 4 }] }),

  // ── H-09 ────────────────────────────────────────────────────
  cosmic_casbah: A({ name: "Cosmic Casbah", kind: "impera", level: 1, khao: 4, anima: 0,
    card: "Your beings gain +1 Lyf, for every Astraea Being you have in combat.",
    effects: [{ maxLyf: 1, per: "astraea" }] }),
  binary_star: A({ name: "Binary Star", kind: "impera", level: 2, khao: 9, anima: 0,
    card: "OP3: Add 2 Astro Beings, up to Lvl 2, from your Grimoire, or Necropolis, to your hand.",
    effects: [{ reviveIlk: "Astraea", count: 2, maxTier: "champion" }] }),
  radiant_anima: A({ name: "Radiant Anima", kind: "impera", level: 3, khao: 7, anima: 0,
    card: "OP2: Nullify 1 Lyt Attack.", effects: [{ status: "nullLyt", dur: 16, on: "self" }] }),
  radiant_shower: A({ name: "Radiant Shower", kind: "uhlti", level: 3, khao: 6, anima: 2,
    card: "*H-09 U'hlti* OPT: Dim the Lyf of your adversary's cards by 1 or their Anima by 2.",
    target: "area", r: 3.6, shape: "star", effects: [{ lyfOrAnima: [1, 2] }] }),

  // ── XULAN ───────────────────────────────────────────────────
  diamond_skin: A({ name: "Diamond Skin", kind: "impera", level: 1, khao: 3, anima: 0,
    card: "This being gains +2 Lyf.", effects: [{ maxLyf: 2 }] }),
  natural_scalpel: A({ name: "Natural Scalpel", kind: "impera", level: 2, khao: 4, anima: 0,
    card: "OP2: When this being Lyt Attacks, dim your adversary's Anima by 1.",
    effects: [{ status: "scalpel", dur: 16, on: "self" }] }),
  eternal_nightmare: A({ name: "Eternal Nightmare", kind: "impera", level: 3, khao: 8, anima: 4,
    card: "OPT: Dim your own Anima by 4; nullify 1 attack on this being or it's Soul.",
    effects: [{ status: "warded", dur: 16, on: "self", charges: 1 }] }),
  sweet_dreams: A({ name: "Sweet Dreams", kind: "uhlti", level: 3, khao: 4, anima: 6,
    card: "*Xulan U'hlti* Dim your adversary's Anima by 1 whenever they Lyt Attack.",
    target: "area", r: 3.6, shape: "void", effects: [{ status: "nightmare", dur: 16, on: "foe" }] }),

  // ════════════════════════════════════════════════════════════
  //  TOMES — the starter set (caP'n picks the rest later). Three
  //  Tome rifts, so three Tomes per Aenarchist. 10 s cooldown each.
  // ════════════════════════════════════════════════════════════
  shower_of_lyt: A({ name: "Shower of Lyt", kind: "tome", school: "Nihil", level: 1, khao: 3, anima: 0,
    card: "Your beings cannot be attacked using Tomes this turn.",
    effects: [{ status: "tomeproof", dur: 8, on: "team" }] }),
  wall_of_lyt: A({ name: "Wall of Lyt", kind: "tome", school: "Nihil", level: 1, khao: 5, anima: 0,
    card: "If your Anima would be dimmed this turn, absorb the damage and add it to your Anima.",
    effects: [{ status: "absorb", dur: 8, on: "self" }] }),
  rays_of_hope: A({ name: "Rays of Hope", kind: "tome", school: "Nihil", level: 1, khao: 3, anima: 0,
    card: "Nullify the effect, or activation, of one of your opponent's cards, except a Nihil Tome, for the rest of the turn; if its an Omnia Tome, destroy it.",
    target: "area", r: 2.6, shape: "light", effects: [{ cleanse: true }] }),
  lyt_footed: A({ name: "Lyt Footed", kind: "tome", school: "Nihil", level: 2, khao: 4, anima: 0,
    card: "Nullify 1 attack being made to 1 of your beings, if the attacking card is an Omnia Tome, ruin it.",
    effects: [{ status: "warded", dur: 8, on: "self", charges: 1 }] }),
  phoenix_aura: A({ name: "Phoenix Aura", kind: "tome", school: "Nihil", level: 3, khao: 8, anima: 0,
    card: "Your cards cannot be attacked using Tomes for 2 turns.",
    effects: [{ status: "tomeproof", dur: 16, on: "team" }] }),
  regnant: A({ name: "Regnant", kind: "tome", school: "Omnia", level: 2, khao: 4, anima: 0,
    card: "Ruin 1 Tome or Curio.",
    target: "area", r: 2.6, shape: "meteor", effects: [{ ruinNonBeings: true }] }),
  lyt_hearted: A({ name: "Lyt Hearted", kind: "tome", school: "Omnia", level: 2, khao: 4, anima: 1,
    card: "Dim your own Anima by 1; ruin 1 card.",
    target: "area", r: 2.6, shape: "meteor", effects: [{ ruinNonBeings: true }, { cleanse: true }] }),
  bhodi_tree: A({ name: "Bhodi Tree", kind: "tome", school: "Construct", level: 1, khao: 1, anima: 0,
    card: "OPT: You gain +1 Anima.",
    target: "area", r: 0.9, effects: [{ construct: { kind: "tree", lyf: 0, every: 8, give: { anima: 1 } } }] }),
  chao_garden: A({ name: "Chao Garden", kind: "tome", school: "Construct", level: 1, khao: 2, anima: 0,
    card: "OPT: Add +1 to your Max Chao at the end of the turn.",
    target: "area", r: 0.9, effects: [{ construct: { kind: "garden", lyf: 0, every: 8, give: { maxKhao: 1 }, cap: 5 } }] }),
  anima_house: A({ name: "Anima House", kind: "tome", school: "Construct", level: 2, khao: 2, anima: 0,
    card: "OP2: You gain +2 Anima.",
    target: "area", r: 1, effects: [{ construct: { kind: "house", lyf: 1, every: 8, give: { anima: 2 }, uses: 2 } }] }),
  chao_duhmdumn_beb: A({ name: "Chao D'uhmdumn Beb", kind: "tome", school: "Construct", level: 1, khao: 2, anima: 0,
    card: "Your adversary must ruin this card before attacking another.",
    target: "area", r: 1, effects: [{ construct: { kind: "duhmdumn", lyf: 0, taunt: true } }] }),
  stars_and_skies: A({ name: "The Stars & Skies", kind: "tome", school: "Construct", level: 3, khao: 8, anima: 0,
    card: "This Tome is Intangible the turn it was constructed. OP3: You gain +5 Anima.",
    target: "area", r: 1.1, effects: [{ construct: { kind: "stars", lyf: 1, every: 8, give: { anima: 5 }, uses: 3, intangibleFor: 8 } }] }),
  gauntlets_azka: A({ name: "Gauntlets of A'zka", kind: "tome", school: "Treasure", level: 1, khao: 2, anima: 0,
    card: "This Treasure belongs to 1 being; it's wielder gains +2 Lyt.",
    effects: [{ treasure: { id: "gauntlets", lyt: 2 } }] }),
  lightning_blade: A({ name: "Lightning Blade", kind: "tome", school: "Treasure", level: 1, khao: 8, anima: 0,
    card: "This Treasure belongs to 1 being; when the wielder Lyt attacks, dim the Lyf of your adversary's cards by 1.",
    effects: [{ treasure: { id: "blade" } }] }),
  sword_of_shadows: A({ name: "Sword of Shadows", kind: "tome", school: "Treasure", level: 2, khao: 6, anima: 0,
    card: "This Treasure belongs to 1 being. OPT: The wielder gains an additional Lyt Attack.",
    effects: [{ treasure: { id: "sword", chargeEvery: 8 } }] }),
  boots_wing_walking: A({ name: "Boots of Wing Walking", kind: "tome", school: "Treasure", level: 2, khao: 6, anima: 0,
    card: "OPT: Make 1 of your beings Intangible.",
    effects: [{ treasure: { id: "boots", phaseEvery: 8 } }] }),
  monkey_suite: A({ name: "Monkey Suite", kind: "tome", school: "Blood Contract", level: 2, khao: 3, anima: 0,
    card: "You and your adversary gain 5 Anima each.", target: "global", effects: [{ bothAnima: 5 }] }),
  devils_advocate: A({ name: "The Devil's Advocate", kind: "tome", school: "Blood Contract", level: 1, khao: 1, anima: 0,
    card: "You and your adversary draw 1 card each.", target: "global", effects: [{ khao: 3 }, { foeKhao: 3 }] }),
};

export const TOME_IDS = Object.keys(ARTS).filter((k) => ARTS[k].kind === "tome");
export const TOME_SLOTS = 3;
export const tomeUrl = (id) => `${process.env.PUBLIC_URL || ""}/cards/tome_${id}.webp`;
export const DEFAULT_TOMES = ["lyt_footed", "bhodi_tree", "gauntlets_azka"];

// What each status means, in one place (the HUD reads this too).
export const STATUS = {
  intangible: { label: "INTANGIBLE", color: "#B388FF", note: "Untouchable. Passes through everything." },
  piercing:   { label: "ANIMA-BOUND", color: "#FF4D8D", note: "Lyt Attacks go straight to Anima." },
  reprisal:   { label: "CHAOS",      color: "#ff6a3a", note: "Whoever Lyt Attacks it dims their own Anima by 1." },
  fate:       { label: "FATE",       color: "#ffe08a", note: "The next Lyt Attack on it: a coin — your side, it's reflected." },
  destined:   { label: "DESTINED",   color: "#ffd24a", note: "Intangible to Impera and U'hltis." },
  hope:       { label: "HOPE",       color: "#9cffb0", note: "+1 Anima whenever a Time being enters combat." },
  bulwark:    { label: "NATURAL LYT", color: "#4ac8ff", note: "Your other beings cannot be touched while this one stands." },
  nullLyt:    { label: "RADIANT",    color: "#ffb04a", note: "Nullifies the next Lyt Attack." },
  scalpel:    { label: "SCALPEL",    color: "#c9b8ff", note: "Its Lyt Attacks also dim Anima by 1." },
  warded:     { label: "WARDED",     color: "#7fe8d8", note: "Nullifies the next attack." },
  nightmare:  { label: "NIGHTMARE",  color: "#9ab8ff", note: "Its Lyt Attacks dim its own Anima by 1." },
  tomeproof:  { label: "TOMEPROOF",  color: "#f2c14e", note: "Cannot be attacked using Tomes." },
  absorb:     { label: "WALL OF LYT", color: "#ffe9a0", note: "Anima it would lose is added instead." },
};
